import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import {
  Box, IconButton, Stack, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import colors from '../../../../../styles/colors';
import MessageBodyField from '../../components/MessageBodyField';
import ImagePicker from '../../components/ImagePicker';
import { dplOptionShape } from '../../components/DplPicker';
import { DPL_OPTIONS_STATUS } from '../../hooks/useDplOptions';
import texts from '../../texts.json';
import {
  BlastCard, BlastNumber, Stepper, StepperButton, StepperValue,
} from '../style';
import BlastDplField from './BlastDplField';

const copy = texts.reengagement.scheduledTexts;

const BlastBlock = ({
  index,
  blast,
  meta,
  dplOptions,
  dplStatus,
  onBodyChange,
  onOffsetChange,
  onDplChange,
  onImageChange,
  onImageError,
  onRemove,
  canRemove,
  coachName,
}) => {
  const {
    body, dayOffset, dpl, image,
  } = blast;
  const { duplicateOffset } = meta;

  const sendSuffix = dayOffset === 1 ? copy.sendSuffixOne : copy.sendSuffixOther;

  return (
    <BlastCard>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <BlastNumber>{index}</BlastNumber>
          <Typography variant="subtitle2" fontWeight={700}>
            {format(copy.textLabel, { index })}
          </Typography>
          <ScheduleRoundedIcon sx={{ fontSize: 16, color: colors.shades.gamma10 }} />
          <Typography variant="body2" color="text.secondary">{copy.sendPrefix}</Typography>
          <Stepper>
            <StepperButton
              type="button"
              onClick={() => onOffsetChange(dayOffset - 1)}
              disabled={dayOffset <= 1}
              aria-label="decrease day offset"
            >
              −
            </StepperButton>
            <StepperValue>{dayOffset}</StepperValue>
            <StepperButton
              type="button"
              onClick={() => onOffsetChange(dayOffset + 1)}
              aria-label="increase day offset"
            >
              +
            </StepperButton>
          </Stepper>
          <Typography
            variant="body2"
            color={duplicateOffset ? 'error' : 'text.secondary'}
            fontWeight={duplicateOffset ? 700 : 400}
          >
            {duplicateOffset ? copy.duplicateOffset : sendSuffix}
          </Typography>
        </Stack>
        {canRemove && (
          <IconButton
            type="button"
            onClick={onRemove}
            aria-label={copy.remove}
            size="small"
            sx={{ color: colors.shades.gamma10 }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Stack>

      <BlastDplField
        options={dplOptions}
        status={dplStatus}
        value={dpl}
        onChange={onDplChange}
      />

      <MessageBodyField
        body={body}
        onBodyChange={onBodyChange}
        minRows={4}
        coachName={coachName}
        hasImage={!!image}
      />

      <Box sx={{ mt: 2 }}>
        <ImagePicker
          image={image}
          onChange={onImageChange}
          onError={onImageError}
          note={copy.imageNote}
        />
      </Box>
    </BlastCard>
  );
};

BlastBlock.propTypes = {
  index: PropTypes.number.isRequired,
  blast: PropTypes.shape({
    body: PropTypes.string,
    dayOffset: PropTypes.number,
    dpl: dplOptionShape,
    image: ImagePicker.propTypes.image,
  }).isRequired,
  meta: PropTypes.shape({
    duplicateOffset: PropTypes.bool,
  }).isRequired,
  dplOptions: PropTypes.arrayOf(dplOptionShape).isRequired,
  dplStatus: PropTypes.oneOf(Object.values(DPL_OPTIONS_STATUS)).isRequired,
  onBodyChange: PropTypes.func.isRequired,
  onOffsetChange: PropTypes.func.isRequired,
  onDplChange: PropTypes.func.isRequired,
  onImageChange: PropTypes.func.isRequired,
  onImageError: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
  coachName: PropTypes.string,
};

BlastBlock.defaultProps = {
  canRemove: true,
  coachName: '',
};

export default BlastBlock;
